import { ISelect } from '@khangroupkz/khan-ui/lib/tabsets/models/select.interface';
import { ObjectItem } from '@core/util/prepareFilter';

export class UtilMethods {
  static createMapBy<T>(arr: T[]): Map<T, T> {
    return arr.reduce((result, item) => result.set(item, item), new Map<T, T>());
  }

  static hasKeysOR<K = any, V = any>(keys: K[], map: Map<K, V>): boolean {
    return keys.some((f) => map.has(f));
  }

  static defaultComparator(a, b): 1 | -1 | 0 {
    if (a === b) {
      return 0;
    }

    const rgx = /^\d+$/;
    if (rgx.test(a) && rgx.test(b)) {
      return a > b ? -1 : 1;
    }

    return a > b ? 1 : -1;
  }

  static sliceMetaBase64Data(base64: string): string {
    let sliceCount = 1;
    while (base64[sliceCount - 1] !== ',') {
      sliceCount++;
    }
    return base64.slice(sliceCount);
  }

  static async imageResizer(canvas: HTMLCanvasElement, src: string, resizeTo: number): Promise<HTMLImageElement> {
    const img = new Image();
    img.src = src;
    await UtilMethods.awaitEvent<HTMLImageElement, void>(img, 'onload');
    let { width, height } = img;
    const maxSide = Math.max(width, height);
    const p = width / height;
    const context = canvas.getContext('2d');
    if (maxSide <= resizeTo) {
      return img;
    }

    if (maxSide > 10000) {
      throw new Error('Изображение слишком большое!');
    }

    if (height === maxSide) {
      height = resizeTo;
      width = height * p;
    } else {
      width = resizeTo;
      height = width / p;
    }
    context.canvas.height = height;
    context.canvas.width = width;
    context.fillStyle = `#fff`;
    context.fillRect(0, 0, width, height);
    context.drawImage(img, 0, 0, width, height);
    img.src = canvas.toDataURL('image/jpeg', 1);
    await UtilMethods.awaitEvent<HTMLImageElement, void>(img, 'onload');
    return img;
  }

  static async imageProportionTransformer(inputEvent: InputEvent): Promise<InputEvent> {
    if (!(inputEvent instanceof Event)) {
      throw new TypeError('inputEvent не является событием');
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const url = URL.createObjectURL((inputEvent.target as HTMLInputElement).files[0]);
    const img = await this.imageResizer(canvas, url, 2000);
    const { width, height } = img;
    const proportion = width / height;
    /**
     * 500 X 470 или 470 X 500 это норма, можно не формировать квадрат а вернуть как есть
     * 690 X 731 тоже норм типа
     * 0.94 это допустимая минимальная пропорция,
     * 1.06 это допустимая максимальная пропорция
     */
    if (proportion > 0.93 && proportion < 1.07) {
      return inputEvent;
    }
    const canvasSize = Math.max(width, height) + 50;
    const dx = (canvasSize - width) / 2;
    const dy = (canvasSize - height) / 2;
    context.canvas.width = canvasSize;
    context.canvas.height = canvasSize;
    context.fillStyle = `#fff`;
    context.fillRect(0, 0, canvasSize, canvasSize);
    context.drawImage(img, dx, dy, width, height);
    const blob = await UtilMethods.awaitEvent<HTMLCanvasElement, Blob>(canvas, 'toBlob', true);
    canvas.remove();
    const file = new File([blob], 'img', {
      type: blob.type,
      lastModified: Date.now(),
    });
    const container = new DataTransfer();
    container.items.add(file);
    (inputEvent.target as HTMLInputElement).files = container.files;
    return inputEvent;
  }

  static awaitEvent<T extends HTMLElement, E>(target: T, event: keyof T, isCallback = false): Promise<E> {
    return new Promise((resolve) => {
      if (isCallback) {
        (target[event] as unknown as (cb: (e: E) => void) => void)(resolve);
      } else {
        (target[event] as unknown as (e: E) => void) = resolve;
      }
    });
  }

  static base64To8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  static hasKeysAND<K = any, V = any>(keys: K[], map: Map<K, V>): boolean {
    return keys.every((x) => map.has(x));
  }

  static extractByNestedKey(selector: string, target: object) {
    if (selector in target) {
      return target[selector];
    }
    return selector.split('.').reduce((currentObject, currentKey) => (currentObject ? currentObject[currentKey] : null), target);
  }

  static baseTotalReducer(val: number[]) {
    return val.reduce((a, b) => a + b);
  }

  static comma(i: number, a: any[]) {
    return i !== a.length - 1 ? ', ' : '';
  }

  static transformIfHasValue<V, ReturnV>(value: V, tranformFunc: (value: V) => ReturnV): ReturnV | null {
    return value ? tranformFunc(value) : null;
  }

  static setToLabelValue<T extends object>(item: T, labelKey: keyof T | string, valueKey: keyof T | string): ISelect {
    return {
      label: UtilMethods.extractByNestedKey(labelKey as string, item),
      value: UtilMethods.extractByNestedKey(valueKey as string, item),
    } as ISelect;
  }

  static sortStringsByABC<T extends ObjectItem>(sortKey: keyof T) {
    return (a: T, b: T) => a[sortKey].localeCompare(b[sortKey]);
  }

  static getArrayWithAllOption<T extends ISelect>(array: T[], labelKey: keyof T = 'label', valueKey: keyof T = 'value'): T[] {
    return [{ [labelKey]: 'common.all', [valueKey]: '' }, ...array] as T[];
  }

  static parseCollectorItems(parseString: string) {
    return parseString
      .split(/[,\s]/g)
      .filter((id) => /^\d+$/.test(id))
      .map((id) => Number(id));
  }
}

export enum HAS_KEY_MODE {
  AND,
  OR,
}
